import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign, Clock } from 'lucide-react';
import EmptyState from '@/components/EmptyState';

const C = { navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C' };
function fmt$(v) { if (!v) return '$0'; if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${v.toLocaleString()}`; }

export default function RenewalInsightsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/analytics/renewal-insights').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;
  if (!data || data.total_renewals === 0) return (
    <div className="space-y-5" data-testid="renewal-insights-page">
      <h1 className="text-2xl font-semibold" style={{ color: C.navy }}>Renewal Insights</h1>
      <EmptyState type="renewals" />
    </div>
  );

  const drift = data.premium_drift;

  return (
    <div className="space-y-5" data-testid="renewal-insights-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>Renewal Insights</h1>
        <p className="text-sm mt-1" style={{ color: C.slate }}>Retention trends, premium drift, churn risk analysis</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total Renewals" value={data.total_renewals} color={C.blue} />
        <KPI label="Retention Rate" value={`${data.retention_rate}%`} color={data.retention_rate >= 80 ? C.green : data.retention_rate >= 50 ? C.amber : C.red} />
        <KPI label="Accepted" value={data.accepted} color={C.green} />
        <KPI label="Declined" value={data.declined} color={C.red} />
        <KPI label="Avg Premium Change" value={`${drift.avg_change_pct > 0 ? '+' : ''}${drift.avg_change_pct}%`} color={drift.avg_change_pct > 5 ? C.amber : C.green} />
      </div>

      {/* Premium Drift */}
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: C.navy }}>Premium Drift Analysis</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Current Book</p><p className="text-lg font-semibold font-mono" style={{ color: C.navy }}>{fmt$(drift.total_current)}</p></div>
          <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Renewal Book</p><p className="text-lg font-semibold font-mono" style={{ color: C.navy }}>{fmt$(drift.total_renewal)}</p></div>
          <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Avg Current</p><p className="text-lg font-semibold font-mono" style={{ color: C.navy }}>{fmt$(drift.avg_current)}</p></div>
          <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Max Increase</p><p className="text-lg font-semibold font-mono" style={{ color: C.red }}>+{drift.max_increase_pct}%</p></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* By Class */}
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: C.navy }}>By Class</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr style={{ background: C.bg }}><th className="text-left py-1.5 px-2" style={{ color: C.slate }}>Class</th><th className="text-right py-1.5 px-2" style={{ color: C.slate }}>Count</th><th className="text-right py-1.5 px-2" style={{ color: C.slate }}>Avg Change</th><th className="text-right py-1.5 px-2" style={{ color: C.slate }}>Premium</th></tr></thead>
              <tbody>
                {data.by_class.map(c => (
                  <tr key={c.class} className="border-t border-[rgba(136,150,168,0.08)]">
                    <td className="py-1.5 px-2 font-medium" style={{ color: C.navy }}>{c.class}</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: C.slate }}>{c.count}</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: c.avg_change > 5 ? C.amber : C.green }}>{c.avg_change > 0 ? '+' : ''}{c.avg_change}%</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: C.navy }}>{fmt$(c.total_premium)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Expiry Window */}
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: C.navy }}>Expiry Windows</h3>
          <div className="space-y-2">
            {data.by_expiry_window.map(w => {
              const urgColor = w.window.startsWith('0') ? C.red : w.window.startsWith('7') ? C.amber : w.window.startsWith('14') ? C.teal : C.green;
              return (
                <div key={w.window} className="flex items-center justify-between p-2 rounded-sm" style={{ background: urgColor + '08' }}>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" style={{ color: urgColor }} />
                    <span className="text-xs font-medium" style={{ color: C.navy }}>{w.window}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-mono" style={{ color: C.slate }}>{w.count} policies</span>
                    <span className="text-xs font-mono font-medium" style={{ color: C.navy }}>{fmt$(w.premium)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Churn Risks */}
      {data.churn_risks?.length > 0 && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: C.amber }} />
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Churn Risk — High Premium Increases</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="churn-risks-table">
              <thead><tr style={{ background: C.bg }}>
                {['Reference', 'Business', 'Class', 'Current', 'Renewal', 'Change', 'Claims', 'Expiry'].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.churn_risks.map(r => (
                  <tr key={r.renewal_reference} className="border-t border-[rgba(136,150,168,0.08)]">
                    <td className="py-2 px-3 font-mono" style={{ color: C.blue }}>{r.renewal_reference}</td>
                    <td className="py-2 px-3 font-medium truncate max-w-[150px]" style={{ color: C.navy }}>{r.business_name}</td>
                    <td className="py-2 px-3" style={{ color: C.slate }}>{r.insurance_class}</td>
                    <td className="py-2 px-3 font-mono" style={{ color: C.navy }}>{fmt$(r.current_premium)}</td>
                    <td className="py-2 px-3 font-mono font-medium" style={{ color: C.navy }}>{fmt$(r.renewal_premium)}</td>
                    <td className="py-2 px-3 font-mono font-medium" style={{ color: C.red }}>+{r.premium_change_pct}%</td>
                    <td className="py-2 px-3" style={{ color: r.claims_count > 0 ? C.red : C.green }}>{r.claims_count}</td>
                    <td className="py-2 px-3" style={{ color: r.days_to_expiry <= 14 ? C.red : C.slate }}>{r.days_to_expiry}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}
